import * as Base64 from "base64-js";
const LITTLE_ENDIAN = true;
const MIN_INT64 = BigInt("-9223372036854775808");
const MAX_INT64 = BigInt("9223372036854775807");
/* eslint-disable @typescript-eslint/no-unused-vars */
function isConvexValue(obj) {
    return (obj instanceof Id ||
        obj === null ||
        typeof obj === "bigint" ||
        typeof obj === "number" ||
        typeof obj === "boolean" ||
        typeof obj === "string" ||
        obj instanceof ArrayBuffer ||
        (obj instanceof Array && obj.every(isConvexValue)) ||
        (obj instanceof Set && [...obj.values()].every(isConvexValue)) ||
        (obj instanceof Map &&
            [...obj.entries()].every(([k, v]) => isConvexValue(k) && isConvexValue(v))) ||
        obj instanceof WeakRef ||
        obj instanceof StrongRef ||
        (obj instanceof Object &&
            Object.entries(obj).every(([_, v]) => isConvexValue(v))));
}
function implementsToConvexValue(obj) {
    return (typeof obj === "object" &&
        obj !== null &&
        "toConvexValue" in obj &&
        obj.toConvexValue instanceof Function);
}
/**
 * Documents in Convex are uniquely identifed by their `Id`, which is accessible on the `_id` field.
 *
 * @public
 */
export class Id {
    constructor(encoded) {
        this.encoded = encoded;
    }
    /**
     * Parse an `Id` from its JSON representation.
     */
    static fromJSON(obj) {
        if (typeof obj.$id !== "string") {
            throw new Error(`Object ${JSON.stringify(obj)} isn't a valid Id`);
        }
        return new Id(obj.$id);
    }
    /**
     * Convert an `Id` into its JSON representation.
     */
    toJSON() {
        return { $id: this.encoded };
    }
    /**
     * Convert an `Id` into a `WeakRef` that can be inserted in another document.
     */
    weakRef() {
        return WeakRef.fromJSON({ $weakRef: this.encoded });
    }
    /**
     * Convert an `Id` into a `StrongRef` that can be inserted in another document.
     */
    strongRef() {
        return StrongRef.fromJSON({ $strongRef: this.encoded });
    }
    /**
     * Parse an `Id` from its string representation.
     */
    static fromString(s) {
        return new Id(s);
    }
    /**
     * Convert an `Id` into its string representation.
     */
    toString() {
        return this.encoded;
    }
    /**
     * Checks if this `Id` refers to the same document as the other `Id`
     *
     * @param - The other `Id` to compare to.
     * @returns `true` if the objects refer to the same document.
     */
    equals(other) {
        if (other) {
            return this.toString() == other.toString();
        }
        return false;
    }
}
/**
 * Strong reference to another document in the Convex backend.
 *
 * @public
 */
export class StrongRef {
    constructor(encoded) {
        this.encoded = encoded;
    }
    /**
     * Parse a `StrongRef` from its JSON representation.
     */
    static fromJSON(obj) {
        if (typeof obj.$strongRef !== "string") {
            throw new Error(`Object ${JSON.stringify(obj)} isn't a valid StrongRef`);
        }
        return new StrongRef(obj.$strongRef);
    }
    /**
     * Convert a `StrongRef` into its JSON representation.
     */
    toJSON() {
        return { $strongRef: this.encoded };
    }
    /**
     * Convert a `StrongRef` back to an `Id`.
     */
    id() {
        return Id.fromJSON({ $id: this.encoded });
    }
}
/**
 * Weak reference to another document in the Convex backend.
 *
 * @public
 */
export class WeakRef {
    constructor(encoded) {
        this.encoded = encoded;
    }
    /**
     * Parse a `WeakRef` from its JSON representation.
     */
    static fromJSON(obj) {
        if (typeof obj.$weakRef !== "string") {
            throw new Error(`Object ${JSON.stringify(obj)} isn't a valid WeakRef`);
        }
        return new WeakRef(obj.$weakRef);
    }
    /**
     * Convert a `WeakRef` into its JSON representation.
     */
    toJSON() {
        return { $weakRef: this.encoded };
    }
    /**
     * Convert a `WeakRef` back to an `Id`.
     */
    id() {
        return Id.fromJSON({ $id: this.encoded });
    }
}
function isSpecial(n) {
    return Number.isNaN(n) || !Number.isFinite(n) || Object.is(n, -0);
}
function validateObjectField(k) {
    if (k.length === 0) {
        throw new Error("Empty fields are disallowed.");
    }
    if (k.startsWith("$")) {
        throw new Error("Field names starting with '$' are reserved.");
    }
}
function jsonToConvexInternal(value) {
    if (value === null) {
        return value;
    }
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "string") {
        return value;
    }
    if (value instanceof Array) {
        return value.map(jsonToConvexInternal);
    }
    if (typeof value !== "object") {
        throw new Error(`Unexpected type of ${value}`);
    }
    const entries = Object.entries(value);
    if (entries.length === 1) {
        const [key, _] = entries[0];
        if (key === "$id") {
            return Id.fromJSON(value);
        }
        if (key === "$bytes") {
            if (typeof value.$bytes !== "string") {
                throw new Error(`Malformed $bytes field on ${value}`);
            }
            return Base64.toByteArray(value.$bytes).buffer;
        }
        if (key === "$integer") {
            if (typeof value.$integer !== "string") {
                throw new Error(`Malformed $integer field on ${value}`);
            }
            const integerBytes = Base64.toByteArray(value.$integer);
            if (integerBytes.byteLength !== 8) {
                throw new Error(`Received ${integerBytes.byteLength} bytes, expected 8 for $integer`);
            }
            let intBytesView = new DataView(integerBytes.buffer);
            return intBytesView.getBigInt64(0, LITTLE_ENDIAN);
        }
        if (key === "$float") {
            if (typeof value.$float !== "string") {
                throw new Error(`Malformed $float field on ${value}`);
            }
            const floatBytes = Base64.toByteArray(value.$float);
            if (floatBytes.byteLength !== 8) {
                throw new Error(`Received ${floatBytes.byteLength} bytes, expected 8 for $float`);
            }
            let floatBytesView = new DataView(floatBytes.buffer);
            const float = floatBytesView.getFloat64(0, LITTLE_ENDIAN);
            if (!isSpecial(float)) {
                throw new Error(`Float ${float} should be encoded as a number`);
            }
            return float;
        }
        if (key === "$set") {
            if (!(value.$set instanceof Array)) {
                throw new Error(`Malformed $set field on ${value}`);
            }
            return new Set(value.$set.map(jsonToConvexInternal));
        }
        if (key === "$map") {
            if (!(value.$map instanceof Array)) {
                throw new Error(`Malformed $map field on ${value}`);
            }
            const map = new Map();
            for (const pair of value.$map) {
                if (!(pair instanceof Array) || pair.length !== 2) {
                    throw new Error(`Malformed pair in $map ${value}`);
                }
                const k = jsonToConvexInternal(pair[0]);
                const v = jsonToConvexInternal(pair[1]);
                map.set(k, v);
            }
            return map;
        }
        if (key === "$weakRef") {
            return WeakRef.fromJSON(value);
        }
        if (key === "$strongRef") {
            return StrongRef.fromJSON(value);
        }
    }
    let out = {};
    for (const [k, v] of Object.entries(value)) {
        validateObjectField(k);
        out[k] = jsonToConvexInternal(v);
    }
    return out;
}
/**
 * Parse a Convex value from its JSON representation. This function will revive classes like
 * {@link Id} that have been serialized to JSON, parse out `BigInt`s, and so on.
 *
 * @param value - The JSON representation of a Convex value previously created with {@link convexToJson}.
 * @returns The JavaScript representation of the Convex value.
 *
 * @public
 */
export function jsonToConvex(value) {
    return jsonToConvexInternal(value);
}
function convexToJsonInternal(value, originalValue, context) {
    if (implementsToConvexValue(value)) {
        value = value.toConvexValue();
    }
    if (value === undefined) {
        throw new Error(`undefined is not a valid Convex value (present at path ${context} in original object ${JSON.stringify(originalValue)})`);
    }
    if (value === null) {
        return value;
    }
    if (value instanceof Id) {
        return value.toJSON();
    }
    if (typeof value === "bigint") {
        if (value < MIN_INT64 || MAX_INT64 < value) {
            throw new Error(`BigInt ${value} does not fit into a 64-bit signed integer.`);
        }
        let buffer = new ArrayBuffer(8);
        new DataView(buffer).setBigInt64(0, value, LITTLE_ENDIAN);
        return { $integer: Base64.fromByteArray(new Uint8Array(buffer)) };
    }
    if (typeof value === "number") {
        if (isSpecial(value)) {
            let buffer = new ArrayBuffer(8);
            new DataView(buffer).setFloat64(0, value, LITTLE_ENDIAN);
            return { $float: Base64.fromByteArray(new Uint8Array(buffer)) };
        }
        else {
            return value;
        }
    }
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        return value;
    }
    if (value instanceof ArrayBuffer) {
        return { $bytes: Base64.fromByteArray(new Uint8Array(value)) };
    }
    if (value instanceof Array) {
        return value.map((value, i) => convexToJsonInternal(value, originalValue, context + `[${i}]`));
    }
    if (value instanceof Set) {
        return {
            $set: [...value].map((value, i) => convexToJsonInternal(value, originalValue, context + `.keys()[${i}]`)),
        };
    }
    if (value instanceof Map) {
        return {
            $map: [...value].map(([k, v], i) => {
                let jsonKey = convexToJsonInternal(k, originalValue, context + `.keys()[${i}]`);
                let jsonValue = convexToJsonInternal(v, originalValue, context + `.values()[${i}]`);
                return [jsonKey, jsonValue];
            }),
        };
    }
    if (value instanceof WeakRef) {
        return value.toJSON();
    }
    if (value instanceof StrongRef) {
        return value.toJSON();
    }
    if (typeof value !== "object") {
        throw new Error(`Unexpected type of ${value} (present at path ${context} in original object ${JSON.stringify(originalValue)})`);
    }
    let out = {};
    for (const [k, v] of Object.entries(value)) {
        validateObjectField(k);
        out[k] = convexToJsonInternal(v, originalValue, context + `.${k}`);
    }
    return out;
}
/**
 * Convert a Convex value to its JSON representation. Use {@ref jsonToConvex} to recreate the original value.
 *
 * @param value - A Convex value to convert into JSON.
 * @returns The JSON representation of `value`.
 *
 * @public
 */
export function convexToJson(value) {
    return convexToJsonInternal(value, value, "");
}
//# sourceMappingURL=values.js.map